// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Table,
  type UsePaginationResult,
} from '@shared/ui-kit/components/grouped-table';
import { type FC, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { type MutualFund } from '@shared/data-access/interfaces/api/ai/screener';
import { Tooltip } from '@mui/material';
import { BuySellButton } from '@advisor-app/features/ui/table/components';
import {
  AssetBrand,
  IconButton,
  CellData,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';

export interface MutualFundsTableProps {
  readonly data: MutualFund[];
  readonly pagination: UsePaginationResult;
  readonly onLoad: (ticker: string) => void;
}

const columnHelper = createColumnHelper<MutualFund>();

export const MutualFundsTable: FC<MutualFundsTableProps> = ({
  data,
  pagination,
  onLoad,
}) => {
  const { t } = useTranslation();
  const { openInvestmentPopup } = useInvestmentPopup();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row.ticker ?? row.isin, {
        header: 'Ticker/Isin',
        cell: ({ getValue }) => (
          <AssetBrand
            ticker={getValue()}
            assetType="mutual_fund"
            analyzePopup={openInvestmentPopup}
            watchlistButton={
              <WatchlistToggleButton ticker={getValue()} assetType="mutual_fund" />
            }
          />
        ),
      }),
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue }) => (
          <Tooltip title={getValue()} placement="top">
            <p className="truncate uppercase">{getValue()}</p>
          </Tooltip>
        ),
        size: 200,
      }),
      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        cell: ({ getValue }) => getValue() ?? '-',
      }),

      columnHelper.accessor('ytd_return', {
        header: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.ytdReturn',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <CellData value={getValue()} format="performance" />
        ),
      }),
      columnHelper.accessor('y1_return', {
        header: t('glossary.investment-returns-1y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.oneYReturn',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <CellData value={getValue()} format="performance" />
        ),
      }),

      columnHelper.accessor('y3_return', {
        header: t('glossary.investment-returns-3y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYReturn',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <CellData value={getValue()} format="performance" />
        ),
      }),

      columnHelper.accessor('y5_return', {
        header: t('glossary.investment-returns-5y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.fiveYReturn',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <CellData value={getValue()} format="performance" />
        ),
      }),

      columnHelper.accessor('y3_vol', {
        header: t('glossary.3y-vol', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYVol',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" />
        ),
      }),

      columnHelper.accessor('max_draw_down', {
        header: t('glossary.max-drawdown', { ns: 'glossary' }),
        meta: {
          tooltip: 'kpis.maxDrawdown',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" emptyValue={-1} />
        ),
      }),
      columnHelper.accessor('expenses_ratio', {
        header: 'TER',
        meta: {
          tooltip: 'common.ter',
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="decimal" emptyValue={-1} />
        ),
      }),
      columnHelper.display({
        id: 'options',
        minSize: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex !gap-4">
            <WatchlistToggleButton
              ticker={row.original.ticker}
              assetType="mutual_fund"
            />
            <Tooltip title={t('common.analyze', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Analysis"
                  variant="icon"
                  data-testid="load-mf-test-id"
                  onClick={() => onLoad(row.original.ticker)}
                />
              </span>
            </Tooltip>
            <BuySellButton
              ticker={row.original.ticker ?? row.original.isin ?? ''}
              operation="buy"
              assetType="mutual_fund"
              dfiUrl={row.original.dfi_url}
            />
          </div>
        ),
      }),
    ],
    [onLoad, openInvestmentPopup, t],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        columns={columns}
        data={data}
        enablePagination
        enableExpanding={false}
        manualPagination
        pageCount={pagination.pageCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={pagination.onPaginationChange}
        emptyState={t('research.mutual-fund-empty-state', { ns: 'research' })}
        initialSorting={[{ id: 'y5_return', desc: true }]}
      />
    </div>
  );
};
